import React, { FC, useState, ChangeEvent } from 'react'
import { useHistory } from 'react-router-dom'
import { useEffectOnce } from 'react-use'
import { Button } from 'transfix-ui/components/Button'
import { Toast } from 'transfix-ui/components/Toast'
import { Box, Typography } from 'transfix-ui/core'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { IUpdateUserInfoPayload } from 'apps/fleet-planner/types'
import { TextInput } from 'components/atoms'
import { ResetPassword, IOnCloseOptions } from './ResetPassword'
import { UserSettingsCard } from './UserSettingsCard'

interface IProfileProps {
  firstName: string
  lastName: string
  onSubmit: (payload: IUpdateUserInfoPayload) => void
}

export const Profile: FC<IProfileProps> = ({
  firstName: firstNameProp,
  lastName: lastNameProp,
  onSubmit,
}) => {
  const deleteAccountEnabled = useFeatureFlag('fmsAccountDeletion')
  const { trackEvent } = useFMSAnalytics()
  const [name, setName] = useState({ firstName: firstNameProp, lastName: lastNameProp })
  const [toast, setToast] = useState({ message: '', isOpen: false })
  const hasFirstAndLastName = !!name?.firstName && !!name?.lastName
  const isDisabled =
    (name?.firstName === firstNameProp && name?.lastName === lastNameProp) || !hasFirstAndLastName
  const closeToastMessageHandler = () => setToast(current => ({ ...current, isOpen: false }))

  const nameChangeHandler = (e: ChangeEvent<HTMLInputElement>) =>
    setName(current => ({
      ...current,
      [e.target.id]: e.target.value,
    }))

  const submithandler = async () => {
    const { firstName, lastName } = name
    try {
      const payload = {
        user: {
          firstName,
          lastName,
        },
      }
      await onSubmit(payload)
      trackEvent('Settings', 'SAVED_PROFILE_SETTINGS', payload)
      setToast({ message: 'Changes Saved.', isOpen: true })
    } catch (e) {
      // no-op
    }
  }

  const closePasswordFormHandler = ({ resetForm: resetPasswordForm }: IOnCloseOptions) => {
    resetPasswordForm()
    setToast({ message: 'Password Reset.', isOpen: true })
  }

  useEffectOnce(() => trackEvent('Settings', 'VIEWED_PROFILE_SETTINGS'))
  const history = useHistory()

  const deleteAccountHandler = () => {
    trackEvent('Settings', 'DELETE_USER_ACCOUNT_SETTINGS')
    history.push('/account-deletion/step/welcome')
  }

  return (
    <>
      <UserSettingsCard title='Name'>
        <div className='row'>
          <div className='col-md-5 mt6'>
            <TextInput
              dataTest='first-name'
              large
              id='firstName'
              onChange={nameChangeHandler}
              value={name?.firstName}
              label='First Name'
              placeholder='First Name'
            />
          </div>
          <div className='col-md-5 mt6'>
            <TextInput
              dataTest='last-name'
              large
              id='lastName'
              onChange={nameChangeHandler}
              value={name?.lastName}
              label='Last Name'
              placeholder='Last Name'
            />
          </div>
        </div>
        <div className='row fr'>
          <div className='mt4 mb4 pa2'>
            <Button
              color='primary'
              data-test='submit-name'
              disabled={isDisabled}
              size='large'
              onClick={submithandler}>
              Save Changes
            </Button>
          </div>
        </div>
      </UserSettingsCard>
      <UserSettingsCard title='Change Password'>
        <div className='row'>
          <ResetPassword onClose={closePasswordFormHandler} hideCancelButton />
        </div>
      </UserSettingsCard>
      {deleteAccountEnabled && (
        <UserSettingsCard title='Delete Your Account'>
          <Box paddingTop='12px'>
            <Typography variant='body2'>
              Deleting your account is permanent and will result in you no longer being able to
              login and access any of your data including load history, documents, and invoices.
              Only users who have no loads in progress or docs needed on delivered loads are
              eligible to delete their accounts.
            </Typography>
          </Box>
          <Box width='100%' marginY='8px' display='flex' justifyContent='right'>
            <Button color='hotPrimary' size='large' onClick={deleteAccountHandler}>
              Delete Account
            </Button>
          </Box>
        </UserSettingsCard>
      )}
      <Box pt={3}>
        <Toast open={toast?.isOpen} onClose={closeToastMessageHandler} message={toast?.message} />
      </Box>
    </>
  )
}
